import React, { useMemo } from "react";
import { Box, Typography, CircularProgress, Alert, useTheme } from "@mui/material";
import dayjs from "dayjs";
import {
  LineChart,
  VerticalBarChart,
  HorizontalBarChart,
  DonutChart,
} from "../../../../../shared/components/charts";
import {
  useMetricsTrends,
  useMetricsDistribution,
} from "../../../../../shared/hooks";
import { transformTrendsData } from "../../shared/utils";
import { metricLabel } from "../utils/metricOptions";
import type { ChartBlock } from "../api/types";

interface ChartBlockViewProps {
  block: ChartBlock;
  clientId: string | null;
  /** When true (print/presentation), suppress the internal fixed height so the
   *  chart fills its page container. */
  fill?: boolean;
}

/**
 * Renders a single chart block bound to live metrics. Line/bar charts use the
 * trends endpoint over the block's rolling window; donut uses the distribution
 * endpoint. All queries are scoped to the active client via clientId.
 */
export const ChartBlockView: React.FC<ChartBlockViewProps> = ({
  block,
  clientId,
  fill = false,
}) => {
  const theme = useTheme();

  const range = useMemo(() => {
    const end = dayjs();
    const start = end.subtract(block.windowDays, "day");
    return {
      start_date: start.format("YYYY-MM-DD"),
      end_date: end.format("YYYY-MM-DD"),
    };
  }, [block.windowDays]);

  const isDistribution = block.chartKind === "donut";

  const trendsQuery = useMetricsTrends({
    metrics: [block.metric],
    granularity: block.granularity,
    client_id: clientId,
    ...range,
  });

  const distributionQuery = useMetricsDistribution({
    metric: block.metric,
    client_id: clientId,
    ...range,
  });

  const query = isDistribution ? distributionQuery : trendsQuery;
  const height = fill ? undefined : 300;

  const trendData = useMemo(
    () =>
      isDistribution
        ? []
        : transformTrendsData(trendsQuery.data, [block.metric]),
    [isDistribution, trendsQuery.data, block.metric],
  );

  const donutData = useMemo(
    () =>
      isDistribution
        ? (distributionQuery.data?.data?.distribution ?? []).map((d) => ({
            label: d.label,
            value: d.value,
          }))
        : [],
    [isDistribution, distributionQuery.data],
  );

  const label = metricLabel(block.metric);

  if (query.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (query.isError) {
    return <Alert severity="error">Couldn't load {label} data.</Alert>;
  }

  const hasData = isDistribution ? donutData.length > 0 : trendData.length > 0;
  if (!hasData) {
    return <Alert severity="info">No {label} data for this period.</Alert>;
  }

  switch (block.chartKind) {
    case "line":
      return (
        <LineChart
          data={trendData}
          xAxisKey="label"
          series={[
            {
              dataKey: block.metric,
              label,
              color: theme.palette.primary.main,
              area: true,
            },
          ]}
          height={height ?? 340}
        />
      );
    case "verticalBar":
      return (
        <VerticalBarChart
          data={trendData.map((row) => ({
            ...row,
            [block.metric]: (row[block.metric] as number | null) ?? 0,
          })) as { [key: string]: string | number }[]}
          xAxisKey="label"
          series={[
            {
              dataKey: block.metric,
              label,
              color: theme.palette.primary.main,
            },
          ]}
          height={height ?? 340}
        />
      );
    case "horizontalBar":
      return (
        <HorizontalBarChart
          data={donutData}
          title={undefined}
          height={height ?? 340}
        />
      );
    case "donut":
      return <DonutChart data={donutData} height={height ?? 340} />;
    default:
      return null;
  }
};
